import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/config/app";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { withAdminCurrency } from "@/utils/adminCurrency";
import { buildUrl } from "@/utils/buildUrl";

import type {
  AdminDinerResponse,
  AdminDinerSubType,
  DinerExtraInformation,
  DinerUpdateStatusPayload,
  IDinerInfo,
  ITopUpRequest,
  ReferralInsightMembersParams,
  ReferralInsightMembersResponse,
  ReferralInsightsSummary,
  TopUpInfoResponse,
  UpdateDinersStatusResponse,
} from "./types";

export const dinersApi = {
  async getAllDiners(params?: PaginationParams): Promise<AdminDinerResponse> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      "/diners",
      withAdminCurrency(params)
    );
    return await apiClient.get<AdminDinerResponse>(url);
  },

  async exportCSVDiners(
    params?: PaginationParams
  ): Promise<{ blob: Blob; filename: string }> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      "/diners/export-csv",
      withAdminCurrency(params)
    );
    return await apiClient.download(url, {
      method: "GET",
    });
  },

  async getDinerById(dinerId: string): Promise<IDinerInfo> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/diners/${dinerId}`,
      withAdminCurrency()
    );
    return await apiClient.get<IDinerInfo>(url);
  },

  async getReferralInsightsSummary(
    dinerId: string,
    currency: string
  ): Promise<ReferralInsightsSummary> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/diners/${dinerId}/referral-insights/summary`,
      { currency }
    );
    return await apiClient.get<ReferralInsightsSummary>(url);
  },

  async getReferralInsightMembers(
    dinerId: string,
    params: ReferralInsightMembersParams
  ): Promise<ReferralInsightMembersResponse> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/diners/${dinerId}/referral-insights/members`,
      params
    );
    return await apiClient.get<ReferralInsightMembersResponse>(url);
  },

  async changeDinersStatus(dinerData: DinerUpdateStatusPayload) {
    const response = await apiClient.patch<UpdateDinersStatusResponse>(
      `${API_ENDPOINTS.ADMIN}/diners/status`,
      dinerData
    );
    return response;
  },

  async getAllBrandDiners(
    brandId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<AdminDinerSubType>> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/diners/brands/${brandId}`,
      params
    );

    return await apiClient.get<PaginatedResponse<AdminDinerSubType>>(url);
  },

  async getAllBranchDiners(
    branchId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<AdminDinerSubType>> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/diners/branches/${branchId}`,
      params
    );

    return await apiClient.get<PaginatedResponse<AdminDinerSubType>>(url);
  },

  async patchDinerDetails(
    dinerId: string,
    payload: Partial<DinerExtraInformation>
  ) {
    const url = `${API_ENDPOINTS.ADMIN}/diners/${dinerId}`;
    return await apiClient.patch<DinerExtraInformation>(url, payload);
  },

  async postAssignDinersToReferralRule(
    dinerId: string,
    referralRuleId: string
  ) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN}/diners/${dinerId}/referral-rules`,
      { referralRuleId }
    );
    return response;
  },

  async getDinerTopUpById(dinerId: string): Promise<TopUpInfoResponse> {
    return await apiClient.get<TopUpInfoResponse>(
      `${API_ENDPOINTS.ADMIN}/diners/${dinerId}/top-up-requests`
    );
  },

  async requestTopUp(dinerId: string, data: ITopUpRequest) {
    return await apiClient.post(
      `${API_ENDPOINTS.ADMIN}/diners/${dinerId}/top-up-requests`,
      data
    );
  },

  async approveTopUp(
    topUpRequestId: string,
    approvedAmount: number
  ): Promise<TopUpInfoResponse> {
    return await apiClient.post<TopUpInfoResponse>(
      `${API_ENDPOINTS.ADMIN}/diners/top-up-requests/${topUpRequestId}/approve`,
      { approvedAmount }
    );
  },

  async rejectTopUp(topUpRequestId: string): Promise<TopUpInfoResponse> {
    return await apiClient.post<TopUpInfoResponse>(
      `${API_ENDPOINTS.ADMIN}/diners/top-up-requests/${topUpRequestId}/reject`
    );
  },
};
